import type { FeatureIconsProps } from '@/pages/solutions/[type]/new-items/feature-icons'
import type { ImageTextItemProps } from '@/pages/solutions/[type]/new-items/image-text'

import banner from '@/assets/img/solution/green-power-plant/banner.jpg'
import imageText_1 from '@/assets/img/solution/green-power-plant/image_text_1.jpg'
import imageText_2 from '@/assets/img/solution/green-power-plant/image_text_2.jpg'
import application_1 from '@/assets/img/solution/green-power-plant/application_1.jpg'
import application_2 from '@/assets/img/solution/green-power-plant/application_2.jpg'
import application_3 from '@/assets/img/solution/green-power-plant/application_3.jpg'
import application_4 from '@/assets/img/solution/green-power-plant/application_4.jpg'
import application_5 from '@/assets/img/solution/green-power-plant/application_5.jpg'

export interface SolutionGreenPowerPlantProps {
  id: number
  title: string
  description: string
  path: string
  banner: string
  featureIcons?: FeatureIconsProps[]
  imageText?: ImageTextItemProps[]
  advantages?: {
    title: string
    image: string
  }
  applications?: {
    title: string
    description?: string | string[]
    image: string
  }[]
}

export const solution_green_power_plant: SolutionGreenPowerPlantProps = {
  id: 4,
  path: '/solution/green-power-plant',
  banner: banner,
  title: 'Idle Land: Green Energy Power Plant (Centralized PV + ESS)',
  description:
    'Centralized PV + ESS builds solar plants on idle land, with storage minimizing waste and stabilizing microgrids. Utility ESS provides backup power during outages, cuts costs via energy arbitrage, and combines eco-friendliness, reliability, and cost-efficiency to support grid modernization and renewable integration.',
  imageText: [
    {
      content: [
        {
          image: imageText_1,
          title: 'Centralized PV + ESS',
          descriptions: [
            'The secondary utilization of idle land (e.g., landfills, abandoned factories, etc.).',
            'Environmental Impact: Lowers carbon emissions and promotes the use of clean energy.',
            'Improves PV Power Utilization: Stores excess electricity to avoid waste and enhance the economic benefits of photovoltaic power generation.',
            'Microgrids: Enhances the reliability and stability of microgrid power supply. The energy storage system can quickly respond to grid demands, participating in peak shaving and frequency regulation to enhance grid operational efficiency.',
          ],
        },
        {
          image: imageText_2,
          title: 'Utility ESS',
          descriptions: [
            'Enhances Power Supply Reliability: In the event of grid failures, the energy storage system can serve as a backup power source to ensure the power supply for critical loads. Smart',
            'Energy Management: Optimizes energy usage through advanced monitoring and control systems.',
            'Enhanced Grid Flexibility: : Enhances the reliability and stability of microgrid power supply. The energy storage system can quickly respond to grid demands, participating in peak shaving and frequency regulation to enhance grid operational efficiency.',
            'Economic Benefits: Reduces energy costs through peak shaving and energy arbitrage.',
          ],
        },
      ],
    },
    {
      title: 'Advantages',
      featureIcons: [
        {
          icon: 'photovoltaicEnergy',
          label: 'Eco-Friendly: Reduces carbon emissions and promotes clean energy.',
          description: 'Ensures rapid and reliable power supply to support critical operations during disasters.',
        },
        {
          icon: 'rapidIncrease',
          label: 'Improved Resilience',
          description: 'Reduces dependency on fragile grid systems and enhances community resilience to disasters.',
        },
        {
          icon: 'sustainabilityPro',
          label: 'Sustainability',
          description: 'Lowers carbon emissions and promotes the use of clean energy.',
        },
        {
          icon: 'increase',
          label: 'Cost-Effectiveness',
          description:
            'Reduces the long-term costs of emergency power provision by utilizing efficient and reusable energy storage systems.',
        },
        {
          icon: 'flexibility',
          label: 'Flexibility',
          description: 'Adaptable to various emergency scenarios and energy demands.',
        },
      ],
    },
  ],
  applications: [
    {
      title: 'Grid-Scale Renewable Energy Projects',
      description:
        'Supports large solar farms that supply electricity to the grid, often in regions with high solar irradiance.',
      image: application_1,
    },
    {
      title: 'Grid Stabilization',
      description: 'Provides ancillary services to maintain grid stability and prevent blackouts.',
      image: application_2,
    },
    {
      title: 'Energy Arbitrage',
      description:
        'Stores solar energy when generation exceeds demand and sells it to the grid during high-price periods.',
      image: application_3,
    },
    {
      title: 'Microgrid Support',
      description: 'Acts as a backbone for microgrids in remote or island areas, ensuring a reliable power supply.',
      image: application_4,
    },
    {
      title: 'Disaster Recovery and Resilience',
      description: 'Provides backup power and grid support in the event of natural disasters or grid failures.',
      image: application_5,
    },
  ],
}
